// Generic Detail-Page für Laufräder / Baugruppen / Montage
// Read-only (bis auf Kommentar) + Stand-Badge + Werkstatt-Link + History

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { moduleApi, type ModuleItem } from '../lib/moduleApi';
import BeistelteileSektion from '../components/BeistelteileSektion';
import BeistelteileBanner from '../components/BeistelteileBanner';
import { KommentarVerlauf } from '../components/KommentarVerlauf';
import { VorkasseBanner } from '../components/VorkasseBanner';

type Modul = 'laufraeder' | 'baugruppen' | 'montage' | 'laser_kant';

const LABELS: Record<Modul, string> = {
  laufraeder: 'Laufräder',
  baugruppen: 'Baugruppen',
  montage: 'Montage',
  laser_kant: 'Laser/Kant',
};

export function ModulDetail({ modul }: { modul: Modul }) {
  const { id } = useParams<{ id: string }>();
  const numId = parseInt(id ?? '0');
  const api = moduleApi(modul);
  const detail = useApi(() => api.get(numId), [numId]);
  const history = useApi(() => api.history(numId), [numId]);

  // Kommentar direkt hier pflegbar — für Hinweise wie "Achtung Lieferung
  // andere Adresse"; mit "Achtung"/"!" wird die Zeile in der Übersicht markiert
  const [kommEdit, setKommEdit] = useState(false);
  const [kommText, setKommText] = useState('');
  const [kommBusy, setKommBusy] = useState(false);

  if (detail.loading) return <div className="p-8 text-slate-500">Lädt …</div>;
  if (detail.error) return <div className="p-8 text-red-600">Fehler: {detail.error.message}</div>;
  if (!detail.data) return null;

  const d = detail.data.data;
  const modulSpecificFields = getModulFields(modul, d);

  const speichereKommentar = async () => {
    setKommBusy(true);
    try {
      await api.update(d.id, { kommentar: kommText.trim() || null, updated_at: d.updated_at } as Partial<ModuleItem>);
      setKommEdit(false);
      detail.reload();
    } catch (err) {
      alert('Speichern fehlgeschlagen: ' + (err as Error).message);
    }
    setKommBusy(false);
  };

  return (
    <div className="p-8 max-w-4xl">
      <Link to={`/${modul}`} className="text-sm text-brand hover:underline">
        ← {LABELS[modul]} Übersicht
      </Link>

      <header className="mt-4 mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase text-slate-500">
            Produktion › {LABELS[modul]}
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {d.bestellnr} {d.position && <span className="text-slate-500">· {d.position}</span>}
          </h1>
          <div className="text-sm text-slate-600 mt-1">
            {d.bezeichnung ?? '—'}
            {d.kunde_name && <span className="ml-2 text-slate-500">· {d.kunde_name}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {d.priority && d.priority !== 'normal' && (
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              d.priority === 'kritisch' ? 'bg-red-100 text-red-800' :
              'bg-amber-100 text-amber-800'
            }`}>
              {d.priority.toUpperCase()}
            </span>
          )}
          {d.stand_wert && (
            <span
              className="px-2 py-0.5 rounded text-xs font-medium"
              style={{ background: d.stand_farbe ?? '#eee', color: '#fff' }}
            >
              {d.stand_wert}
            </span>
          )}
          {d.fertig === 1 && (
            <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-xs font-semibold">
              ✓ FERTIG
            </span>
          )}
        </div>
      </header>

      <BeistelteileBanner auftragModul={modul} auftragId={numId} />

      <VorkasseBanner modul={modul} recordId={numId} vorkasse={d.vorkasse} bezahltAm={d.vorkasse_bezahlt_am}
                       onChanged={() => detail.reload()} />

      {/* Auftragsdaten */}
      <section className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <h2 className="font-semibold text-slate-900 mb-3">Auftragsdaten</h2>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <KV label="Bestellnr" value={d.bestellnr} />
          <KV label="DV-Nr" value={d.dv_nummer} />
          <KV label="Position" value={d.position} />
          <KV label="Kunde" value={d.kunde_name} />
          <KV label="Sachbearbeiter" value={d.sachbearbeiter_name} />
          <KV label="Zeichnungs-Nr" value={d.zeichnungs_nr} />
          <KV label="Stück" value={String(d.stueck)} />
          <KV label="Lieferdatum" value={d.lieferdatum?.slice(0, 10)} />
          <KV label="Zeit/Stück" value={d.zeit_pro_stueck ? `${d.zeit_pro_stueck}h` : '—'} />
          <KV label="Gesamtzeit" value={`${d.gesamtzeit ?? 0}h`} />
          <KV label="KW" value={String(d.kw ?? '—')} />
          <KV label="Fertigungsdatum" value={d.fertigungsdatum?.slice(0, 10) ?? '—'} />
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs uppercase text-slate-500">Kommentar</div>
            {!kommEdit && (
              <button onClick={() => { setKommText(d.kommentar ?? ''); setKommEdit(true); }}
                      className="text-xs text-brand hover:underline">✎ Bearbeiten</button>
            )}
          </div>
          {kommEdit ? (
            <div>
              <textarea value={kommText} onChange={e => setKommText(e.target.value)} rows={3}
                        className="w-full border border-slate-300 rounded-lg p-2 text-sm"
                        placeholder={'z. B. "Achtung Lieferung andere Adresse" — mit Achtung/! wird die Zeile in der Übersicht hervorgehoben'} />
              <div className="flex gap-2 mt-2">
                <button onClick={speichereKommentar} disabled={kommBusy}
                        className="bg-brand text-white rounded-md px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                  {kommBusy ? 'Speichert …' : 'Speichern'}
                </button>
                <button onClick={() => setKommEdit(false)} className="text-sm text-slate-500 hover:underline">Abbrechen</button>
              </div>
            </div>
          ) : d.kommentar ? (
            <div className={/achtung/i.test(d.kommentar) || d.kommentar.trim().startsWith('!')
              ? 'text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3'
              : 'text-sm text-slate-700'}>
              {(/achtung/i.test(d.kommentar) || d.kommentar.trim().startsWith('!')) && '⚠ '}{d.kommentar}
            </div>
          ) : (
            <div className="text-sm text-slate-400">—</div>
          )}
        </div>
      </section>

      {/* Modul-spezifische Felder */}
      {modulSpecificFields.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
          <h2 className="font-semibold text-slate-900 mb-3">Modul-Spezifisch</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            {modulSpecificFields.map(f => (
              <KV key={f.label} label={f.label} value={f.value} />
            ))}
          </div>
        </section>
      )}

      {/* Aktionen */}
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <Link
          to={`/werkstatt?modul=${modul}`}
          className="bg-brand text-white px-4 py-2 rounded-md font-medium hover:bg-sky-600"
        >
          In Werkstatt öffnen →
        </Link>
        {d.fertig === 1 ? (
          <button
            onClick={async () => {
              if (!confirm(`Auftrag "${d.bestellnr}" wieder ÖFFNEN?\nFertigungsdatum/Werker werden zurückgesetzt, Stand → Offen.`)) return;
              try {
                await moduleApi(modul).update(d.id, {
                  fertig: 0,
                  updated_at: d.updated_at,
                } as Partial<ModuleItem>);
                detail.reload();
              } catch (err) {
                alert('Wieder-öffnen fehlgeschlagen: ' + (err as Error).message);
              }
            }}
            className="bg-amber-100 text-amber-800 border border-amber-300 px-4 py-2 rounded-md font-medium hover:bg-amber-200"
          >
            ↶ Wieder öffnen
          </button>
        ) : (
          <span className="text-xs text-slate-500">
            Status-Änderung über Werkstatt-Modus
          </span>
        )}
      </div>

      <BeistelteileSektion auftragModul={modul} auftragId={numId} />

      <KommentarVerlauf modul={modul} recordId={numId} />

      {/* History */}
      {history.data && history.data.data.length > 0 && (
        <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-slate-900 mb-3">Verlauf</h2>
          <ul className="space-y-1 text-xs text-slate-600 max-h-64 overflow-y-auto">
            {history.data.data.slice(0, 20).map((h, i) => (
              <li key={i} className="border-b border-slate-50 py-1.5">
                <pre className="whitespace-pre-wrap text-[11px]">{JSON.stringify(h, null, 2)}</pre>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="text-slate-800 font-medium">{value ?? '—'}</div>
    </div>
  );
}

function getModulFields(modul: Modul, d: ModuleItem): { label: string; value: string | null }[] {
  const toStr = (v: unknown): string | null =>
    v === null || v === undefined || v === '' ? null : typeof v === 'boolean' ? (v ? 'Ja' : '—') : String(v);
  switch (modul) {
    case 'laufraeder':
      return [
        { label: 'Material-Typ', value: toStr(d.material_typ) },
        { label: 'Wuchten', value: toStr(d.wuchten) },
        { label: 'Lackieren', value: toStr(d.lackieren) },
        { label: 'RAL-Farbe', value: toStr(d.ral_farbe) },
        { label: 'Einschweißnabe', value: toStr(d.einschweissnabe) },
        { label: 'ATEX', value: toStr(d.atex_beschriftung) },
        { label: 'VT/PT', value: toStr(d.vt_pt) },
      ].filter(f => f.value !== null);
    case 'baugruppen':
      return [
        { label: 'Lackiert', value: toStr(d.lackiert) },
        { label: 'RAL-Farbe', value: toStr(d.ral_farbe) },
        { label: 'Schweißer', value: toStr(d.schweisser) },
      ].filter(f => f.value !== null);
    case 'montage':
      return [
        { label: 'RAL-Farbe', value: toStr(d.ral_farbe) },
        { label: 'Person Gefertigt', value: toStr(d.person_gefertigt) },
      ].filter(f => f.value !== null);
    case 'laser_kant':
      return [
        { label: 'Lackieren', value: toStr(d.lackieren) },
        { label: 'RAL-Farbe', value: toStr(d.ral_farbe) },
        { label: 'Lieferadresse', value: toStr(d.lieferadresse) },
        { label: 'Abgerechnet', value: toStr(d.abgerechnet) },
      ].filter(f => f.value !== null);
  }
}
